/**
 * `/v1/securities/:ticker` and its two fan-out routes, in local mode (plan §6.6).
 *
 * Local mode is the one place `consensus` carries values. Live v1 has no
 * licensed consensus provider. Catalysts are synthetic headlines here and
 * dated Yahoo events or `[]` on HTTP (ADR-0039); both adapters render
 * documented empty states on a miss (plan §6.5a). That asymmetry is
 * asserted per adapter and deliberately kept OUT of the shared port-conformance
 * factory, which asserts shape parity, not value parity (plan §4.7).
 */
import type {
  AreaId,
  DiversificationPayload,
  PeersPayload,
  SecurityProfile,
  SeriesQuery,
  Ticker,
} from '@meridian/contracts';

import { computeCatalysts } from '../../analytics/catalysts';
import { computeConsensus } from '../../analytics/consensus';
import { rankByCorrelation } from '../../analytics/correlation';
import { correlationExtremesFromRanked } from '../../analytics/correlation-extremes';
import { diversificationGroupsFromRanked } from '../../analytics/diversification';
import { selectIndustryPeers, selectPeers } from '../../analytics/peers';
import { quoteClose } from '../../analytics/quote-close';
import { syntheticAvgVolume, syntheticFinancials } from '../../analytics/synthetic-financials';
import { liveIndustryName } from '../../constants/industries';
import { CORRELATION_EXTREMES_SIZE, PEER_PANEL_SIZE } from '../../constants/panels';
import { generateSeries } from '../../series/generate-series';
import { seriesStats } from '../../series/series-stats';
import { toWireSecurity, type DerivedSecurity } from '../../universe/derive-security';

import type { SeriesCache } from '../../series/series-cache';

/** 52-week statistics are, definitionally, the one-year window. */
const STATS_WINDOW = '1Y' as const;

export function buildProfile(security: DerivedSecurity, cache: SeriesCache): SecurityProfile {
  /*
   * `generateSeries` emits closes only, so `seriesStats` sees no volumes and
   * returns `avgVolume: null`; the synthetic float-turnover draw supplies it
   * instead. The `volumes` parameter is not dead — `apps/api`'s security
   * service passes real vendor volumes through it — it simply has no local
   * source, which is why the override reads as a second producer here.
   */
  const bars = generateSeries(security, STATS_WINDOW, cache);
  const stats = seriesStats(bars);
  /* Derived from the profile's OWN 52-week series, never from whichever window
     the chart happens to be showing — that coupling is what made the header's
     change move when the range selector did. */
  const quote = quoteClose(bars);
  if (quote === null) throw new Error('buildProfile: empty series');
  return {
    security: toWireSecurity(security),
    quote,
    stats: { ...stats, avgVolume: syntheticAvgVolume(security.ticker, security.sharesOut) },
    consensus: computeConsensus(security),
    catalysts: computeCatalysts(security, STATS_WINDOW),
    financials: syntheticFinancials(security.ticker, security.rev, security.pe),
  };
}

export function buildPeers(
  rows: readonly DerivedSecurity[],
  security: DerivedSecurity,
  query: SeriesQuery,
  assigned: ReadonlyMap<string, AreaId>,
): PeersPayload {
  const areaId = assigned.get(security.ticker) ?? null;
  const sector = selectPeers(security, rows, query.window, PEER_PANEL_SIZE).map(toWireSecurity);
  /* `assignIndustries` classifies every row, so this branch is unreachable
     through the local adapter today (see `./area.ts` header). It is kept — not
     as a guard but as the payload shape — because `areaId` is structurally
     nullable and a future assignment that leaves a row unclassified must emit
     the unpublished-membership payload rather than a bogus identity. */
  if (areaId === null) {
    return { sector, industry: [], industryId: null, industryName: null };
  }
  /* Carry the source row on the projection rather than mapping tickers back
     through a second Map: `selectIndustryPeers` is generic over its row type
     and returns the very objects it was handed, so the ranked rows still hold
     their `DerivedSecurity`. That removes a full-universe Map per call and the
     unreachable miss-branch the lookup would otherwise need. */
  const candidates = rows.map((row) => ({
    ticker: row.ticker,
    sc: row.sc,
    ret: row.ret,
    areaId: assigned.get(row.ticker) ?? null,
    row,
  }));
  const subject = {
    ticker: security.ticker,
    sc: security.sc,
    ret: security.ret,
    areaId,
    row: security,
  };
  const ranked = selectIndustryPeers(subject, candidates, query.window, PEER_PANEL_SIZE);
  return {
    sector,
    industry: ranked.map((peer) => toWireSecurity(peer.row)),
    industryId: areaId,
    industryName: liveIndustryName(areaId),
  };
}

export function buildDiversifiers(
  rows: readonly DerivedSecurity[],
  security: DerivedSecurity,
  query: SeriesQuery,
): DiversificationPayload {
  const candidates = rows.filter((r) => r.ticker !== security.ticker);
  /* One pairwise scan feeds both the extremes shortlists and the three groups
     (contracts: same pairwise correlation definition and shared scan). */
  const ranked = rankByCorrelation(security, candidates);
  const { most, least } = correlationExtremesFromRanked(ranked, CORRELATION_EXTREMES_SIZE);
  return {
    ticker: security.ticker,
    window: query.window,
    /* `core` returns readonly arrays; the wire schemas type them mutable. The
       spread is the boundary copy, not a cast — the same reason
       `toWireSecurity` rebuilds `idx`/`f`: handing a consumer the array core
       computed over makes an in-place sort three layers away corrupt this
       payload. */
    groups: [...diversificationGroupsFromRanked(security, ranked, query.window)],
    most: [...most],
    least: [...least],
  };
}

export function indexByTicker(
  rows: readonly DerivedSecurity[],
): ReadonlyMap<Ticker, DerivedSecurity> {
  return new Map(rows.map((r) => [r.ticker, r]));
}
